import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import actions from '@src/redux/actions';
import { LANGUAGE } from '@src/constants';
import { FEATURE } from '@src/constants/package';
import route from '@src/constants/route';
import CrownIcon from '@src/assets/icons/icon-crown.svg';
import Tooltip from '@src/components/Tooltip';
import CommonButton from '@src/new-components/Button';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import Typography from '@src/new-components/Typography';
import ProjectAIOptions from '@src/containers/Project/ProjectInput/UploadProjectFilePopup/ProjectAIOptions';
import usePresentationAIModels from '@src/hooks/usePresentationAIModels';
import useIsAutoSaveOverLimit from '@src/hooks/useIsAutoSaveOverLimit';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import useAINarrationUpgrade from '@src/hooks/useAINarrationUpgrade';
import { hasActiveFeature } from '@src/services/package';
import { MOE_SURFACE } from '@src/constants/moengage';
import PopupUpPkgUseRewrite from '@src/containers/ProjectDetail/Dubbing/Content/PopupUpPkgUseRewrite';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { PROJECT_PRODUCT } from '@src/constants/project';
import { COLOR } from '@src/styles/color';
import {
  isNoteChanged,
  hasNarrationConfig,
  getExportingState,
} from '@src/services/project';
import { calculatePresentationCredit } from '@src/utils/presentation';
import { generateScripts } from '@src/apis/project';

const DEFAULT_LANGUAGE = 'vi';

const AIGenerateDialog = ({
  open,
  onClose,
  targetBlockId = null,
  onSaveProject,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useDispatch();

  const { languagesV3 } = useSelector((state) => state.language);
  const projectId = useSelector((state) => state.project.projectId);
  const source = useSelector((state) => state.project.source);
  const exports = useSelector((state) => state.project.exports);
  const isDownloading = useSelector((state) => state.project.isDownloading);
  const { blocks = {}, blockIds = [] } = useSelector(
    (state) => state.project.multiBlock || {},
  );
  const user = useSelector((state) => state.auth.user);

  // Narration generation is a paid feature: without it the backend rejects the
  // request and the user only ever sees the generic error toast.
  const hasAIPresentationAccess = hasActiveFeature({
    user,
    feature: FEATURE.AI_GENERATE_NARRATION,
  });

  const { upgradePkgName, targetPackageCode } = useAINarrationUpgrade();
  const [openPopupUpPkg, setOpenPopupUpPkg] = useState(false);

  const { getFeatureValue } = useFeatureFlags();
  const exportHistoryEnabled = useExportHistoryEnabled();
  const exportBannerEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );
  const { isExporting } = getExportingState({
    exports,
    isDownloading,
    product: PROJECT_PRODUCT.PRESENTATION,
  });

  const { models, getDescription } = usePresentationAIModels();
  // Over the limit under auto-save → project can't be saved, so the
  // narration-generation (a DB-writing action) must be disabled.
  const isAutoSaveOverLimit = useIsAutoSaveOverLimit();
  const [aiConfig, setAiConfig] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasNoteChanged = useMemo(
    () =>
      hasNarrationConfig(source) && Object.values(blocks).some(isNoteChanged),
    [blocks, source],
  );

  const isSingleMode = Boolean(targetBlockId);

  const targetBlockIds = useMemo(() => {
    if (isSingleMode) return [targetBlockId];
    if (hasNarrationConfig(source)) {
      return blockIds.filter((id) => isNoteChanged(blocks[id]));
    }
    return blockIds;
  }, [isSingleMode, targetBlockId, source, blockIds, blocks]);

  const showNoteChangedWarning = !isSingleMode && hasNoteChanged;
  const isRegenerate = isSingleMode || hasNoteChanged;

  const languages = useMemo(() => {
    if (!Array.isArray(languagesV3)) return [];
    return [...languagesV3]
      .filter((item) => item.value !== 'multilingual')
      .sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        const labelA = a.label?.[currentLang] || '';
        const labelB = b.label?.[currentLang] || '';
        return labelA.localeCompare(labelB);
      })
      .map((item) => ({
        ...item,
        label: item.label?.[currentLang] || item.label?.[LANGUAGE.EN],
      }));
  }, [languagesV3, currentLang]);

  useEffect(() => {
    if (!open) return;
    if (!languagesV3?.length) dispatch(actions.language.getLanguagesV3());
  }, [open, languagesV3, dispatch]);

  useEffect(() => {
    if (!open) return;
    setAiConfig({ language: DEFAULT_LANGUAGE, model: models[0] || null });
  }, [open, models]);

  useEffect(() => {
    if (open) return;
    setAiConfig(null);
    // Otherwise reopening the dialog shows the upsell again and refires its hit
    setOpenPopupUpPkg(false);
  }, [open]);

  const creditCost = useMemo(
    () =>
      calculatePresentationCredit({
        enableAI: true,
        slideCount: targetBlockIds.length,
        model: aiConfig?.model,
      }),
    [targetBlockIds, aiConfig?.model],
  );

  const creditCostLabel = creditCost
    ? t('costsCreditBold', { value: creditCost.toLocaleString(currentLang) })
    : '';

  // Same upsell content as the PPTX upload popup: HTML body + inline link to
  // the payment page, named after the cheapest package carrying the feature.
  const upgradeTooltip = (
    <span>
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: t('descriptionUnlockPremiumAIFeaturesNow', {
            pkgName: upgradePkgName,
          }),
        }}
      />{' '}
      <Link
        to={route.PAYMENT}
        style={{ color: '#fff', textDecoration: 'underline' }}
      >
        {t('buyNow')}
      </Link>
    </span>
  );

  const handleSubmit = async () => {
    if (isSubmitting) return;
    // No package access → upsell instead of a save + a rejected request
    if (!hasAIPresentationAccess) {
      setOpenPopupUpPkg(true);
      return;
    }

    const showError = () =>
      dispatch(
        actions.noti.push({ severity: 'error', message: t('errorOccurred') }),
      );

    setIsSubmitting(true);
    try {
      try {
        const saveResponse = await onSaveProject?.();
        if (!saveResponse?.status) {
          showError();
          return;
        }
      } catch {
        showError();
        return;
      }

      try {
        const response = await generateScripts(projectId, {
          blockIds: targetBlockIds,
          language: aiConfig.language,
          model: aiConfig.model?.value,
        });
        dispatch(
          actions.project.setIsGeneratingNarration(true, targetBlockIds),
        );
        if (response.status) {
          onClose?.();
        } else {
          dispatch(actions.project.setIsGeneratingNarration(false));
          showError();
        }
      } catch {
        dispatch(actions.project.setIsGeneratingNarration(false));
        showError();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open || !aiConfig) return null;

  const noteChangedWarning = showNoteChangedWarning ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: COLOR.orangeV2[8],
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <WarningRoundedIcon
        sx={{ width: 20, height: 20, color: COLOR.orange, flexShrink: 0 }}
      />
      <Typography variant="body-14-regular" color={COLOR.descriptionGray}>
        {t('aiPresentationNoteChangedWarning')}
      </Typography>
    </Box>
  ) : null;

  return (
    <>
      <PopupUpPkgUseRewrite
        open={openPopupUpPkg}
        onClose={() => setOpenPopupUpPkg(false)}
        pkgName={upgradePkgName}
        targetPackageCode={targetPackageCode}
        featureKey={FEATURE.AI_GENERATE_NARRATION}
        surface={MOE_SURFACE.PROJECT_EDITOR}
        product={PROJECT_PRODUCT.PRESENTATION}
      />
      <CommonDialog
        open={open}
        onClose={onClose}
        title={t('aiPresentationScript')}
        width="512px"
        disableAutoClose
        contentComponent={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              width: '100%',
            }}
          >
            <Typography variant="body-14-regular" color={COLOR.descriptionGray}>
              {t('aiPresentationScriptDescription')}
            </Typography>
            <ProjectAIOptions
              value={aiConfig}
              onChange={setAiConfig}
              languages={languages}
              models={models}
              renderModelDescription={getDescription}
              renderModelCredit={(model) =>
                model?.creditPerSlide
                  ? t('creditPerSlide', { value: model.creditPerSlide })
                  : null
              }
              showSwitch={false}
              languageLabel={t('selectOutputLanguage')}
              modelLabel={t('selectModel')}
              languagePlaceholder={t('selectLanguage')}
              searchPlaceholder={t('search')}
              tipText={
                showNoteChangedWarning ? null : t('aiPresentationTimeTip')
              }
            >
              {noteChangedWarning}
            </ProjectAIOptions>
          </Box>
        }
        actionComponent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '12px',
              width: '100%',
            }}
          >
            <Typography
              variant="body-14-regular"
              color={COLOR.darkGray}
              style={{ textAlign: 'right' }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: creditCostLabel }}
            />
            {!hasAIPresentationAccess && (
              <Tooltip title={upgradeTooltip} placement="top" arrow>
                <img
                  src={CrownIcon}
                  alt="crown"
                  style={{ width: 20, height: 20, cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            <CommonButton
              variant="contained"
              bgrDisabled={COLOR.yellow.default}
              disabled={
                isSubmitting ||
                isAutoSaveOverLimit ||
                (exportBannerEnabled && isExporting && !exportHistoryEnabled)
              }
              startIcon={
                isSubmitting ? (
                  <CircularProgress
                    size={18}
                    thickness={3.6}
                    sx={{ color: COLOR.darkBlue }}
                  />
                ) : undefined
              }
              customStyle={{
                height: 40,
                padding: '8px 16px',
                borderRadius: '8px',
                minWidth: 'auto',
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              onClick={handleSubmit}
            >
              {isRegenerate
                ? t('regenerateNarrationButton')
                : t('createNarrationButton')}
            </CommonButton>
          </Box>
        }
      />
    </>
  );
};

export default AIGenerateDialog;
